import { useEffect, useMemo, useState } from 'react';
import { ENERGIES, USAGES, fmt, isValidRatio, ratioTotal, setUsageRatio, usageRatio } from './domain';
import { parseNum } from './sheet';
import { PC, UMR, caption } from './tokens';
import type { EnergyKey, RatioTable as Ratios, UsageKey } from './types';

interface Props {
  value: Ratios;
  defaults: Ratios;
  editing: boolean;
  /** Colonne Total masquée dans les ratios modifiables. */
  showTotal?: boolean;
  onChange: (next: Ratios) => void;
  onValidityChange?: (valid: boolean) => void;
}

const th: React.CSSProperties = {
  padding: '6px 8px', height: 40, fontSize: 12, lineHeight: '16px', fontWeight: 700, color: UMR.textLabel,
  background: UMR.grey100, borderBottom: `1px solid ${UMR.border}`, textAlign: 'center', verticalAlign: 'middle',
};
const td: React.CSSProperties = {
  padding: '6px 8px', height: 34, fontSize: 13, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
  borderBottom: `1px solid ${UMR.borderSubtle}`,
};
const input: React.CSSProperties = {
  width: '100%', height: 28, background: UMR.white, border: `1px solid ${UMR.border}`,
  font: `400 13px/20px ${UMR.font}`, textAlign: 'right', padding: '0 6px',
  fontVariantNumeric: 'tabular-nums', color: UMR.black,
};

const key = (u: UsageKey, e: EnergyKey) => `${u}.${e}`;

/** Ratios de consommation par typologie, tous millésimes confondus (kWh/m²/an). */
export function RatioSummaryTable({ value, defaults, editing, showTotal = true, onChange, onValidityChange }: Props) {
  const [texts, setTexts] = useState<Record<string, string>>({});
  useEffect(() => { if (!editing) setTexts({}); }, [editing]);

  const invalid = useMemo(
    () => Object.entries(texts).filter(([, t]) => !isValidRatio(parseNum(t))).map(([k]) => k),
    [texts],
  );
  useEffect(() => { onValidityChange?.(invalid.length === 0); }, [invalid.length, onValidityChange]);

  /** Saisie confirmée (blur ou Entrée) : évite d'enchaîner les mises à l'échelle à chaque frappe. */
  const commit = (u: UsageKey, e: EnergyKey) => {
    const k = key(u, e);
    const raw = texts[k];
    if (raw === undefined) return;
    const n = parseNum(raw);
    if (!isValidRatio(n)) return;
    setTexts((t) => { const next = { ...t }; delete next[k]; return next; });
    if (n !== usageRatio(value, u)[e]) onChange(setUsageRatio(value, u, e, n));
  };

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', fontFamily: UMR.font }}>
      <colgroup>
        <col style={{ width: 200 }} />
        {ENERGIES.map((e) => <col key={e.key} />)}
        {showTotal && <col style={{ width: 110 }} />}
      </colgroup>
      <thead>
        <tr>
          <th style={{ ...th, textAlign: 'left' }}>Typologie</th>
          {ENERGIES.map((e) => <th key={e.key} style={th}>{e.label}</th>)}
          {showTotal && <th style={{ ...th, color: PC.accentStrong }}>Total</th>}
        </tr>
      </thead>
      <tbody>
        {USAGES.map((u) => {
          const row = usageRatio(value, u.key);
          const base = usageRatio(defaults, u.key);
          return (
            <tr key={u.key}>
              <td style={{ ...td, textAlign: 'left', fontWeight: 700 }}>{u.label}</td>
              {ENERGIES.map((e) => {
                const k = key(u.key, e.key);
                const text = texts[k] ?? String(row[e.key]);
                const bad = invalid.includes(k);
                const changed = row[e.key] !== base[e.key];
                return (
                  <td key={e.key} style={{ ...td, padding: editing ? 3 : td.padding, background: !editing && changed ? PC.accentBg : undefined }}>
                    {editing ? (
                      <input
                        type="text"
                        inputMode="decimal"
                        aria-label={`${u.label} — ${e.label}`}
                        value={text}
                        style={{ ...input, borderColor: bad ? PC.dangerText : UMR.border, color: changed ? PC.accentStrong : UMR.black }}
                        onChange={(ev) => setTexts((t) => ({ ...t, [k]: ev.target.value }))}
                        onBlur={() => commit(u.key, e.key)}
                        onKeyDown={(ev) => { if (ev.key === 'Enter') ev.currentTarget.blur(); }}
                      />
                    ) : (
                      <span style={{ color: changed ? PC.accentStrong : undefined, fontWeight: changed ? 700 : 400 }}>{fmt(row[e.key], 1)}</span>
                    )}
                  </td>
                );
              })}
              {showTotal && <td style={{ ...td, fontWeight: 700, color: PC.accentStrong, background: PC.accentBg }}>{fmt(ratioTotal(row), 1)}</td>}
            </tr>
          );
        })}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={ENERGIES.length + (showTotal ? 2 : 1)} style={{ ...caption, padding: '6px 8px' }}>
            kWh/m²/an
            {invalid.length > 0 && <span style={{ color: PC.dangerText }}> · {invalid.length} valeur(s) hors plage 1 – 1 000</span>}
          </td>
        </tr>
      </tfoot>
    </table>
  );
}
